/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import TurnstileGate from '../../components/security/TurnstileGate';
import useCaptchaConfig from '../../hooks/useCaptchaConfig';
import './AdminLogin.css';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, verify2FA, isAuthenticated } = useAdminAuth();

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [backupCodeInput, setBackupCodeInput] = useState('');
  const [countdown, setCountdown] = useState(300); // 5 minutes
  
  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Refs for 2FA input focus
  const codeInputRefs = useRef([]);

  // Captcha (Cloudflare Turnstile). Gated by server: /api/public/config
  // tells us whether to require it. Fresh fetch on every page load so a
  // Render env flip takes effect on the next navigation for everyone.
  const captchaCfg = useCaptchaConfig();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaStatus, setCaptchaStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const captchaGateRef = useRef(null);
  const needsCaptcha = captchaCfg.ready && captchaCfg.captchaEnabled;
  const captchaBlocks = needsCaptcha && (!captchaToken || captchaStatus !== 'ready');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Countdown timer for 2FA
  useEffect(() => {
    let timer;
    if (requires2FA && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      // Session expired
      handleReset2FA();
      setError('2FA session expired. Please login again.');
    }
    return () => clearInterval(timer);
  }, [requires2FA, countdown]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (needsCaptcha && !captchaToken) {
      setError('Please complete the security check.');
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.username, formData.password, captchaToken);
      // Turnstile tokens are single-use — consume it whether or not the
      // login succeeded so the next attempt starts fresh.
      if (captchaToken) {
        setCaptchaToken(null);
        if (captchaGateRef.current) captchaGateRef.current.reset();
      }

      if (result.requires2FA) {
        // Move to 2FA step
        setRequires2FA(true);
        setTempToken(result.data.tempToken);
        setCountdown(result.data.expiresIn || 300);
        
        // Focus first code input
        setTimeout(() => {
          codeInputRefs.current[0]?.focus();
        }, 100);
      } else if (result.success) {
        // No 2FA, login complete
        navigate('/admin/dashboard');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...twoFACode];
    newCode[index] = value;
    setTwoFACode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handle2FASubmit(fullCode);
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newCode = [...twoFACode];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setTwoFACode(newCode);
        
        if (digits.length === 6) {
          handle2FASubmit(newCode.join(''));
        }
      });
    }
  };

  const handle2FASubmit = async (code = null) => {
    setError('');
    setLoading(true);

    const codeToSubmit = isBackupCode ? backupCodeInput : (code || twoFACode.join(''));

    if (!codeToSubmit || (isBackupCode ? codeToSubmit.length < 8 : codeToSubmit.length !== 6)) {
      setError(isBackupCode ? 'Please enter a valid backup code' : 'Please enter all 6 digits');
      setLoading(false);
      return;
    }

    try {
      const result = await verify2FA(tempToken, codeToSubmit, isBackupCode);

      if (result.success) {
        // Check for backup code warning
        if (result.data?.backupCodeWarning) {
          // Could show a toast or alert here
          console.warn(result.data.backupCodeWarning);
        }
        navigate('/admin/dashboard');
      } else {
        setError(result.error || 'Invalid 2FA code');
        // Clear code inputs
        if (!isBackupCode) {
          setTwoFACode(['', '', '', '', '', '']);
          codeInputRefs.current[0]?.focus();
        }
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset2FA = () => {
    setRequires2FA(false);
    setTempToken('');
    setTwoFACode(['', '', '', '', '', '']);
    setIsBackupCode(false);
    setBackupCodeInput('');
    setCountdown(300);
    setError('');
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ========== 2FA VERIFICATION SCREEN ==========
  if (requires2FA) {
    return (
      <div className="admin-login-container">
        <div className="security-banner">
          <div className="security-icon">🔐</div>
          <div className="security-text">
            <strong>Two-Factor Authentication</strong> - Enter your verification code
          </div>
        </div>

        <div className="admin-login-card">
          <div className="login-header">
            <div className="logo-container">
              <img src="/logo.jpg" alt="SkillDelivery Logo" className="platform-logo" />
            </div>
            <h2 className="admin-title">Verify Your Identity</h2>
            <p className="admin-subtitle">
              {isBackupCode 
                ? 'Enter one of your backup codes' 
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="countdown-timer" style={{
            textAlign: 'center',
            marginBottom: '20px',
            padding: '10px',
            background: countdown < 60 ? '#fef2f2' : '#f0f9ff',
            borderRadius: '8px',
            color: countdown < 60 ? '#dc2626' : '#0369a1'
          }}>
            ⏱️ Code expires in: <strong>{formatCountdown(countdown)}</strong>
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {!isBackupCode ? (
            // TOTP Code Input
            <div className="totp-input-container" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px'
            }}>
              {twoFACode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => codeInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  disabled={loading}
                  style={{
                    width: '48px',
                    height: '56px',
                    textAlign: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0891b2'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              ))}
            </div>
          ) : (
            // Backup Code Input
            <div className="form-group">
              <label htmlFor="backupCode">Backup Code</label>
              <input
                type="text"
                id="backupCode"
                value={backupCodeInput}
                onChange={(e) => setBackupCodeInput(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                disabled={loading}
                style={{ textAlign: 'center', letterSpacing: '2px', fontFamily: 'monospace' }}
              />
            </div>
          )}

          <button
            className="login-button"
            onClick={() => handle2FASubmit()}
            disabled={loading}
            style={{ marginBottom: '16px' }}
          >
            {loading ? (
              <span><span className="spinner"></span> Verifying...</span>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Toggle Backup Code Mode */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <button
              type="button"
              onClick={() => {
                setIsBackupCode(!isBackupCode);
                setError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#0891b2',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px'
              }}
            >
              {isBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
            </button>
          </div>

          {/* Back to Login */}
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleReset2FA}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Back to login
            </button>
          </div>

          <div className="security-info">
            <div className="info-item">
              <span className="info-icon">📱</span>
              <span>Open Google Authenticator or your 2FA app</span>
            </div>
            <div className="info-item">
              <span className="info-icon">🔢</span>
              <span>Enter the 6-digit code shown for SkillDelivery</span>
            </div>
          </div>
        </div>

        <div className="login-background"></div>
      </div>
    );
  }

  // ========== REGULAR LOGIN SCREEN ==========
  return (
    <div className="admin-login-container">
      {/* Security Banner */}
      <div className="security-banner">
        <div className="security-icon">🔒</div>
        <div className="security-text">
          <strong>Restricted Access:</strong> Admin Console - Authorized Personnel Only
        </div>
      </div>

      {/* Login Card */}
      <div className="admin-login-card">
        {/* Logo/Header */}
        <div className="login-header">
          <div className="logo-container">
            <img
              src="/logo.jpg"
              alt="SkillDelivery Logo"
              className="platform-logo"
            />
            <p className="tagline">Skills, Delivered!</p>
          </div>
          <h2 className="admin-title">Admin Console</h2>
          <p className="admin-subtitle">Platform Management & Training Pipeline</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username or Email</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
              placeholder="Enter your username"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {needsCaptcha && (
            <div className="form-group">
              <TurnstileGate
                ref={captchaGateRef}
                onToken={setCaptchaToken}
                onStatus={setCaptchaStatus}
              />
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={loading || captchaBlocks}
          >
            {loading ? (
              <span>
                <span className="spinner"></span> Authenticating...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Security Info */}
        <div className="security-info">
          <div className="info-item">
            <span className="info-icon">🔐</span>
            <span>Two-Factor Authentication Enabled</span>
          </div>
          <div className="info-item">
            <span className="info-icon">⏰</span>
            <span>2-hour session timeout</span>
          </div>
          <div className="info-item">
            <span className="info-icon">📊</span>
            <span>All actions are audit logged</span>
          </div>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p>© 2025 SKD RATWANI TECHNOLOGY & SOFTWARE SOLUTIONS PRIVATE LIMITED</p>
          <p className="footer-links">
            <a href="/privacy">Privacy Policy</a> •
            <a href="/terms">Terms of Service</a> •
            <a href="mailto:security@skilldelivery.co.in">Report Security Issue</a>
          </p>
        </div>
      </div>

      {/* Background Pattern */}
      <div className="login-background"></div>
    </div>
  );
};

export default AdminLogin;
